import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit3, Trash2, Mail, Linkedin, Globe, Calendar, Users, AlertCircle } from 'lucide-react';
import type { TeamMember } from '@/hooks/useTeamMembers';

interface TeamSectionProps {
  members: TeamMember[];
  isLoading: boolean;
  error: any;
  isAdminMode: boolean;
  onCreateMember: () => void;
  onEditMember: (member: TeamMember) => void;
  onDeleteMember: (id: string) => void;
}

export const TeamSection: React.FC<TeamSectionProps> = ({
  members,
  isLoading,
  error,
  isAdminMode,
  onCreateMember,
  onEditMember,
  onDeleteMember
}) => {
  if (!members) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="team-section">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">Gestão da Equipa</h2>
          {isLoading && (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
          )}
        </div>
        <button
          onClick={onCreateMember}
          className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Adicionar Membro
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error || 'Erro ao carregar membros da equipa. Por favor, tente novamente.'}</p>
        </div>
      ) : members.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-lg p-6 border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-4 text-center"
            onClick={onCreateMember}
          >
            <Plus className="w-12 h-12 text-primary" />
            <p className="text-lg font-semibold">Adicionar Novo Membro</p>
            <p className="text-sm text-gray-400">Clique para criar</p>
          </motion.div>

          {members.map((member, index) => (
            <motion.div
              key={member.id}
              data-member-id={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -12, transition: { duration: 0.2 } }}
              className="relative bg-card/20 backdrop-blur-[2px] rounded-2xl p-6 shadow-xl border border-white/5 overflow-hidden group h-full flex flex-col hover:shadow-2xl hover:shadow-primary/20 hover:border-primary/50 transition-all duration-300"
            >
              <div className="relative w-full aspect-square max-h-48 shrink-0 mb-4 rounded-xl overflow-hidden">
                <img
                  src={member.image || '/placeholder-member.jpg'}
                  alt={`Foto de ${member.name}`}
                  className="w-full h-full object-cover transition-all duration-300"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder-member.jpg';
                    console.log('Error loading member image, using placeholder');
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>

              <div className="flex flex-col flex-grow">
                <div className="flex justify-between items-start">
                  <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                    {member.role || 'Membro'}
                  </span>
                </div>

                <h3 className="text-xl font-semibold mt-3 mb-2">{member.name}</h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">{member.description}</p>

                <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {member.created_at ? new Date(member.created_at).toLocaleDateString('pt-PT', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    }) : 'Data não disponível'}
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {member.projects_count || 0} {member.projects_count === 1 ? 'projeto' : 'projetos'}
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      title="Email"
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  )}
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {member.website && (
                    <a
                      href={member.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      title="Website"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>

              {isAdminMode && (
                <div className="mt-auto pt-4">
                  <div className="h-px bg-white/10 mb-4" />
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => onEditMember(member)}
                      className="flex items-center gap-2 px-6 py-2.5 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      Editar
                    </button>
                    <button
                      onClick={() => onDeleteMember(member.id)}
                      className="flex items-center gap-2 px-6 py-2.5 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Apagar
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-400">Nenhum membro encontrado. Adicione um novo membro para começar.</p>
        </div>
      )}

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        onClick={onCreateMember}
        className="md:hidden fixed right-4 bottom-20 z-50 flex flex-col items-center px-3 py-2 bg-primary text-primary-foreground rounded-lg shadow-lg hover:bg-primary/90 transition-colors text-xs font-medium w-[100px]"
      >
        <Plus className="w-3.5 h-3.5 mb-0.5" />
        <span className="text-center leading-tight">Adicionar Membro</span>
      </motion.button>
    </div>
  );
};